/**
 * @file Date utility functions for the platform app
 */

/**
 * Format a date to a human-readable string
 * @param date - The date to format
 * @returns A formatted date string (e.g., "Jan 15, 2023")
 */
export function formatDate(date: Date | string): string {
  if (!date) return "N/A";
  
  const dateObj = typeof date === "string" ? new Date(date) : date;
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return "Invalid date";
  
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(dateObj);
}

/**
 * Format a date to include time
 * @param date - The date to format
 * @returns A formatted date and time string (e.g., "Jan 15, 2023, 3:45 PM")
 */
export function formatDateTime(date: Date | string): string {
  if (!date) return "N/A";
  
  const dateObj = typeof date === "string" ? new Date(date) : date;
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return "Invalid date";
  
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  }).format(dateObj);
}

/**
 * Format a relative time (e.g., "2 days ago")
 * @param date - The date to format
 * @returns A relative time string
 */
export function formatRelativeTime(date: Date | string): string {
  if (!date) return "N/A";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return "Invalid date";

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const format = (count: number, unit: string) =>
    `${count} ${unit}${count === 1 ? '' : 's'} ago`;

  if (diffInSeconds < 60) return format(diffInSeconds, 'second');
  if (diffInSeconds < 3600) return format(Math.floor(diffInSeconds / 60), 'minute');
  if (diffInSeconds < 86400) return format(Math.floor(diffInSeconds / 3600), 'hour');
  if (diffInSeconds < 2592000) return format(Math.floor(diffInSeconds / 86400), 'day');
  if (diffInSeconds < 31536000) return format(Math.floor(diffInSeconds / 2592000), 'month');

  return format(Math.floor(diffInSeconds / 31536000), 'year');
}

/**
 * Formats an ISO date string into a relative/readable label
 * "Today", "Yesterday", or "12 Jan" / "31 Dec" format
 */
export const formatDateLabel = (isoDate: string): string => {
  const date = new Date(isoDate);
  const now = new Date();

  const stripTime = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = stripTime(now);
  const target = stripTime(date);
  const diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';

  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  return `${day} ${month}`;
};

/**
 * Coarse date buckets for list-of-conversations UIs (ChatGPT-style
 * sidebar grouping). Boundaries are measured in whole calendar days so a
 * chat from earlier "today" doesn't fall into "yesterday" just because the
 * clock crossed midnight in the middle of the session.
 */
export type DateGroup = 'today' | 'yesterday' | 'previous-7-days' | 'older';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Midnight (local time) of the given date — anchor for calendar-day math. */
const startOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Number of days ago (0 = today, 1 = yesterday). */
const diffCalendarDays = (isoDate: string, now: Date = new Date()): number => {
  const date = new Date(isoDate);
  return Math.floor(
    (startOfLocalDay(now).getTime() - startOfLocalDay(date).getTime()) / MS_PER_DAY
  );
};

export const getDateGroup = (isoDate: string, now: Date = new Date()): DateGroup => {
  const diff = diffCalendarDays(isoDate, now);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff <= 7) return 'previous-7-days';
  return 'older';
};

/** Human-readable heading per bucket. */
export const DATE_GROUP_LABELS: Record<DateGroup, string> = {
  'today': 'Today',
  'yesterday': 'Yesterday',
  'previous-7-days': 'Previous 7 days',
  'older': 'Older',
};

/** Deterministic render order — buckets absent from the current data render nothing. */
export const DATE_GROUP_ORDER: DateGroup[] = [
  'today',
  'yesterday',
  'previous-7-days',
  'older',
];

export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The accepted input as a usable Date, or null when it is absent or unreadable.
 */
export function toValidDate(date: Date | string | undefined | null): Date | null {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return Number.isNaN(dateObj.getTime()) ? null : dateObj;
}

/**
 * Local midnight of the given date, for whole-day difference maths. Shared so
 * that a row's "Today" label and a "Today" filter can never disagree on where
 * the day boundary sits.
 */
export function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * A friendly absolute stamp: "Today, 10:09 AM", "Yesterday, 1:01 PM",
 * "Jun 26, 7:03 AM", gaining the year once the date leaves the current one.
 *
 * Deliberately not `formatRelativeTime` below, which renders the verbose
 * "2 hours ago" form. Both are wanted in different places, so they are kept
 * apart rather than one drifting into the other.
 */
export function formatDayStamp(date: Date | string | undefined | null): string {
  const dateObj = toValidDate(date);
  if (!dateObj) return '';

  const now = new Date();
  const time = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dayDiff = Math.round((startOfDay(now) - startOfDay(dateObj)) / DAY_MS);
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;

  const day = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(dateObj.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
  return `${day}, ${time}`;
}
